import dayjs from 'dayjs';

/**
 * How near an expiry has to be before the wallet calls a voucher "Expiring".
 *
 * The wallet used to treat "has an expiry date" as "expiring", with no window at
 * all. A voucher valid for another year sat in the Expiring tab under an amber
 * warning tag, and — worse — was left out of Available, so the tab a member
 * reads as "what I can use" was quietly missing usable vouchers.
 */
export const EXPIRING_SOON_DAYS = 7;

export const hasExpiry = voucher => Boolean(voucher?.expiresAt);

/**
 * Expired either because a date we hold has passed, or because the issuing
 * system says so.
 *
 * The status arm exists for IDR collection passes: IDR owns their expiry (end of
 * day, Singapore time) and reports it on the order, but the wallet row carries
 * no date of its own — so a date-only check could never see it, and an expired
 * pass showed as Available until someone was turned away at the counter.
 */
export const isExpired = voucher =>
  voucher?.status === 'expired' ||
  (hasExpiry(voucher) && dayjs(voucher.expiresAt).isBefore(dayjs()));

/** Inside the window and not gone yet — the only case that warrants a warning. */
export const isExpiringSoon = voucher =>
  hasExpiry(voucher) &&
  !isExpired(voucher) &&
  dayjs(voucher.expiresAt).isBefore(dayjs().add(EXPIRING_SOON_DAYS, 'day'));

/**
 * Whole days until the expiry, rounded up, so an expiry eight hours away reads
 * as one day rather than none.
 */
export const daysUntilExpiry = voucher => {
  if (!hasExpiry(voucher)) return null;
  return Math.ceil(dayjs(voucher.expiresAt).diff(dayjs(), 'hour') / 24);
};

/**
 * A countdown rather than a date, for the last few days: "in 5 days" is easier
 * to act on than "19 Sep" when the deadline is close.
 */
export const expiringSoonText = voucher => {
  const days = daysUntilExpiry(voucher);
  if (days === null) return '';
  if (days <= 1) {
    return dayjs(voucher.expiresAt).isSame(dayjs(), 'day') ? 'Expires today' : 'Expires tomorrow';
  }
  return `Expires in ${days} days`;
};

/** A partner collection pass is collected at IDR's counter; everything else is
 *  settled in the app. The distinction changes the WORD the member reads. */
export const isPartnerPass = voucher => voucher?.source === 'partner';

export const isRedeemed = voucher => voucher?.status === 'redeemed';

/**
 * A partner pass whose order was cancelled. It stays in the wallet rather than
 * disappearing, because the partner treats a cancelled order as recoverable.
 */
export const isCancelled = voucher => voucher?.status === 'cancelled';

/**
 * The one place a voucher's state becomes words and a colour.
 *
 * Lives here rather than in a component because it had drifted twice: the card
 * and the detail screen each carried their own ladder, and the same voucher read
 * differently depending on which screen you were looking at. Extracting the
 * expiry predicates alone was not enough — what actually diverged was the
 * mapping built on top of them.
 *
 * Order matters. Cancelled and redeemed are terminal and outrank expiry: a pass
 * collected before it lapsed was collected, and saying otherwise rewrites
 * history.
 */
export const statusLabel = voucher => {
  // "Unavailable", not "Cancelled": the member did not cancel anything, and the
  // partner treats the order as recoverable, so the softer word is also the more
  // accurate one. The stored status stays `cancelled`.
  if (isCancelled(voucher)) return 'Unavailable';
  if (isRedeemed(voucher)) return isPartnerPass(voucher) ? 'Collected' : 'Used';
  if (isExpired(voucher)) return 'Expired';
  // Only within the window. A warning tag on a voucher good for another year is
  // what taught members to ignore the amber tag.
  if (isExpiringSoon(voucher)) return 'Expiring';
  return 'Available';
};

export const statusTagVariant = voucher => {
  // Inert rather than alarming — nothing went wrong that the member did.
  if (isCancelled(voucher) || isRedeemed(voucher)) return 'default';
  if (isExpired(voucher)) return 'error';
  if (isExpiringSoon(voucher)) return 'warning';
  return 'success';
};

/** The line under the status: what happened, or what is about to. */
export const expiryText = voucher => {
  if (isCancelled(voucher)) return 'Not available to collect right now';
  if (isRedeemed(voucher) && voucher.redeemedAt) {
    const on = dayjs(voucher.redeemedAt).format('D MMM YYYY');
    return `${isPartnerPass(voucher) ? 'Collected' : 'Used'} on ${on}`;
  }
  // Close to the deadline, a countdown is easier to act on than a date.
  if (isExpiringSoon(voucher)) return expiringSoonText(voucher);
  if (hasExpiry(voucher)) {
    const on = dayjs(voucher.expiresAt).format('D MMM YYYY');
    return `${isExpired(voucher) ? 'Expired' : 'Expires'} ${on}`;
  }
  // An IDR pass can be expired with no date of its own to show.
  if (isExpired(voucher)) return 'Expired';
  // Nothing to report: no deadline, nothing collected, nothing cancelled.
  return '';
};
